import React from 'react';
import PropTypes from 'prop-types';
import TextCounter from './TextCounter';
import CircleCounter from './CircleCounter';
import FlipCounter from './FlipCounter';
import { resolve, select } from '@cvent/nucleus-dynamic-css';
import { resolveTestId } from '@cvent/nucleus-test-automation';
/**
# Countdown Component
**/
export default class Countdown extends React.Component {
  static displayName = 'Countdown';
  static propTypes = {
    /** When the countdown should end.  */
    endDate: PropTypes.instanceOf(Date).isRequired,
    /** Flag indicating whether to show days value in the countdown. */
    showDays: PropTypes.bool,
    /** Label for the days digit. */
    daysLabel: PropTypes.string,
    /** Flag indicating whether to show hours value in the countdown. */
    showHours: PropTypes.bool,
    /** Label for the hours digit. */
    hoursLabel: PropTypes.string,
    /** Flag indicating whether to show minutes value in the countdown. */
    showMinutes: PropTypes.bool,
    /** Label for the minutes digit. */
    minutesLabel: PropTypes.string,
    /** Flag indicating whether to show seconds value in the countdown. */
    showSeconds: PropTypes.bool,
    /** Label for the seconds digit. */
    secondsLabel: PropTypes.string,
    /** The type of counter to display. */
    type: PropTypes.oneOf(['circle', 'flip', 'text']),
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Top level style applied to the ul element. */
      countdown: PropTypes.object,
      /** Style applied to CircleCounter component. */
      circle: PropTypes.object,
      /** Style applied to FlipCounter component. */
      flip: PropTypes.object,
      /** Style applied to TextCounter component. */
      text: PropTypes.object
    })
  };
  static defaultProps = {
    showDays: true,
    showHours: true,
    showMinutes: true,
    showSeconds: true,
    type: 'circle'
  };
  constructor(props) {
    super(props);
    this.getCoundownStateFromEndDate = this.getCoundownStateFromEndDate.bind(this);
    this.clearCountdown = this.clearCountdown.bind(this);
    this.startCountdown = this.startCountdown.bind(this);
    this.state = this.getCoundownStateFromEndDate(props.endDate);
    this.countdownTimer = null;
  }
  componentDidMount() {
    this.startCountdown();
  }
  componentWillReceiveProps(nextProps) {
    if (nextProps.endDate.getTime() !== this.props.endDate.getTime()) {
      this.clearCountdown();
      this.setState(this.getCoundownStateFromEndDate(nextProps.endDate));
      this.startCountdown();
    }
  }
  componentWillUnmount() {
    this.clearCountdown();
  }
  getCoundownStateFromEndDate(endDate) {
    if (endDate <= (new Date())) {
      return {
        daysLeft: 0,
        hoursLeft: 0,
        minutesLeft: 0,
        secondsLeft: 0,
        millisecondsLeft: 0,
        inProgress: false
      };
    }
    let milliseconds = (endDate.getTime() - new Date().getTime());
    const days = parseInt(milliseconds / 86400000, 10);
    milliseconds = milliseconds % 86400000;
    const hours = parseInt(milliseconds / 3600000, 10);
    milliseconds = milliseconds % 3600000;
    const minutes = parseInt(milliseconds / 60000, 10);
    milliseconds = milliseconds % 60000;
    const seconds = parseInt(milliseconds / 1000, 10);
    milliseconds = parseInt(milliseconds % 1000, 10);
    return {
      daysLeft: days,
      hoursLeft: hours,
      minutesLeft: minutes,
      secondsLeft: seconds,
      millisecondsLeft: milliseconds,
      inProgress: true
    };
  }
  clearCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
  startCountdown() {
    if (this.state.inProgress) {
      setTimeout(() => {
        this.setState(this.getCoundownStateFromEndDate(this.props.endDate));
        this.countdownTimer = setInterval(() => {
          if (this.state.inProgress) {
            this.setState(this.getCoundownStateFromEndDate(this.props.endDate));
          } else {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
          }
        }, 1000);
      }, this.state.millisecondsLeft);
    }
  }
  render() {
    let CounterComponent;
    switch (this.props.type) {
      case 'circle':
        CounterComponent = CircleCounter;
        break;
      case 'flip':
        CounterComponent = FlipCounter;
        break;
      case 'text':
        CounterComponent = TextCounter;
        break;
      default:
        throw new Error('Provided countdown type is not supported: ' + this.props.type);
    }
    return (
      <ul {...resolveTestId(this.props)} {...resolve(this.props, 'countdown')}>
        {this.props.showDays ?
          <CounterComponent
            {...select(this.props, this.props.type)}
            label={this.props.daysLabel}
            digit={this.state.daysLeft}
            total={30}
            type="days"
          /> : null
        }
        {this.props.showHours ?
          <CounterComponent
            {...select(this.props, this.props.type)}
            label={this.props.hoursLabel}
            digit={this.state.hoursLeft}
            total={24}
            type="hours"
          /> : null
        }
        {this.props.showMinutes ?
          <CounterComponent
            {...select(this.props, this.props.type)}
            label={this.props.minutesLabel}
            digit={this.state.minutesLeft}
            total={60}
            type="minutes"
          /> : null
        }
        {this.props.showSeconds ?
          <CounterComponent
            {...select(this.props, this.props.type)}
            label={this.props.secondsLabel}
            digit={this.state.secondsLeft}
            total={60}
            type="seconds"
          /> : null
        }
      </ul>
    );
  }
}



// WEBPACK FOOTER //
// ./~/nucleus-core/counters/Countdown.js